/**
 * MUSIC GATE KEEPER（/mgk）の静的コンテンツ。
 * 旧 Django SSR（app/templates/creator/mgk.html）からの移植。
 */

import mgkWorkflow1 from "../../assets/images/mgk/mgk_workflow1.webp";
import mgkWorkflow2 from "../../assets/images/mgk/mgk_workflow2.webp";
import mgkWorkflow3 from "../../assets/images/mgk/mgk_workflow3.webp";

export const MGK_TITLE = "Music Gate Keeper";
export const MGK_SUBTITLE = "類似曲著作権リサーチ";

export const MGK_CATCHPHRASE = "どんな名作も、誰かのインスパイアで出来ている";
export const MGK_CATCH_DESCRIPTION = [
  "あなたの作品が既存の楽曲と類似していないかを調査しましょう。",
  "私たちは、オリジナリティを保ち、名作を誤って損なわないためのサービスを提供しています。",
];

export const MGK_BODY_PARAGRAPHS = [
  "オリジナルの映画やゲーム作品において、音楽は作品の魅力を引き立てる非常に重要な要素です。しかし、意図せず既存の作品と類似してしまったり、それを連想させてしまう場合、作品の独自性や価値が損なわれるリスクがあります。このような問題を未然に防ぐことは、制作ディレクターやコンポーザーにとって大きな課題です。無意識のうちに既存の作品から影響を受けることがあり、それに気づきにくいケースも多々あります。",
  "そこで、私たちは「MGK（Music Gate Keeper）サービス」をご提案します。専用の WEB ページに音源をアップロードするだけで、類似した曲を簡単にリサーチできます。既存の原盤や類似メロディ、さらに市場に出ていないカスタムコンテンツとも照合し、類似率、利用範囲、利用時間といった詳細なレポートをご提供します。",
];

export interface MgkWorkflowStep {
  title: string;
  image: string;
  body: string;
  note?: string;
}

export const MGK_WORKFLOW: MgkWorkflowStep[] = [
  {
    title: "あなたの作品をアップロード",
    image: mgkWorkflow1,
    body: "専用アカウントでサインインして、作品をアップロードしましょう。ドラッグ & ドロップするだけで、分析をスタートできます。オーディオファイルとビデオファイルに対応しています。",
  },
  {
    title: "あなたの作品を分析",
    image: mgkWorkflow2,
    body: "楽曲の照合には数分かかります。既存の楽曲と何らかの一致性がある場合は、指紋アイコンが青にかわります。",
    note: "※ アイコンが消えたら、一致する楽曲がないことを意味しています。",
  },
  {
    title: "類似した曲を検出",
    image: mgkWorkflow3,
    body: "指紋アイコンをクリックして、結果を確認しましょう。もし原盤が利用されていると、照合率（%）と利用範囲、利用時間が確認できます。メロディが似ているかどうか、複製（カバーソング）の候補もチェックしてみてください。",
  },
];

/** 説明資料（public/ 配下に置き、ハッシュなしで配信） */
export const MGK_PAMPHLET_URL = "/pdf/MusicGateKeeper.pdf";

/** ティーザー動画（HLS）。Os.jsx と同じく viteStaticCopy で配信される絶対パス */
export const MGK_TEASER_HLS = "/assets/media/hls/mgk_teaser/master.m3u8";

// ===== プラン =====

export type MgkPlanId = "standard" | "pro";

export interface MgkPlan {
  id: MgkPlanId;
  name: string;
  summary: string;
  features: { icon: string; text: string }[];
  /** 月額（税込）。個別見積の場合は undefined */
  monthlyPrice?: number;
}

export const MGK_PLANS: MgkPlan[] = [
  {
    id: "standard",
    name: "STANDARD",
    summary: "ツールのみ",
    features: [
      { icon: "person", text: "5 アカウント（ID、パスワード）" },
      { icon: "storage", text: "御社専用クラウドサーバー" },
      { icon: "calendar_clock", text: "10 ファイル（時間上限 10 分）/ 日" },
    ],
    monthlyPrice: 110000,
  },
  {
    id: "pro",
    name: "PRO",
    summary: "ツール & ヒューマンリサーチ",
    features: [
      { icon: "person", text: "5 アカウント（ID、パスワード）" },
      { icon: "storage", text: "御社専用クラウドサーバー" },
      { icon: "calendar_clock", text: "30 ファイル（時間上限 30 分）/ 日" },
    ],
  },
];

export const formatMgkPrice = (plan: MgkPlan): string =>
  plan.monthlyPrice !== undefined
    ? `${plan.monthlyPrice.toLocaleString("ja-JP")}円（税込）/ 月`
    : "個別見積";

/** お問い合わせ API に渡す種別。管理者宛メールの件名にも入る */
export const MGK_CONTACT_CATEGORY = "MUSIC GATE KEEPER";
